import { useEffect } from 'react';
import ChatbotStore from '@/store/ChatbotStore';
import StationStore from '@/store/Station/StationStore';
import { initAIAssistantSocket } from '@/sockets/aiAssistantSocket';
import { SocketChatResponse, SocketErrorResponse } from '@/types/chatbot.types';
import epaxios from '@/utils/epaxios';

interface UseAISocketProps {
  chatbotStore: ChatbotStore;
  stationStore: StationStore;
}

interface SiteDetails {
  customerAccountedNumber?: string;
  accountManagerFullName?: string;
}

export const useAISocket = ({ chatbotStore, stationStore }: UseAISocketProps) => {
  useEffect(() => {
    const stationId = stationStore?.currentStation?.id || chatbotStore.stationContext?.stationId;
    if (!stationId) {
      return;
    }

    const handleSocketMessage = (response: SocketChatResponse | SocketErrorResponse) => {
      chatbotStore.handleSocketResponse(response);
    };

    const handleConnect = () => {
      console.log('AI Assistant socket connected');
    };

    const handleDisconnect = () => {
      console.log('AI Assistant socket disconnected');
    };

    // Fetch site details and initialize socket
    const initializeSocket = async () => {
      let siteDetails: SiteDetails | null = null;

      // Fetch site details for the station
      try {
        const { data } = await epaxios.get(`value-dashboard/insights/assistant/station/${stationId}/site`);
        siteDetails = data;
      } catch (error) {
        console.error('Failed to fetch site details:', error);
        // Continue with socket connection even if site details fetch fails
      }

      // Prepare site details for socket connection
      const socketSiteDetails = siteDetails
        ? {
            salesforceAccountId: siteDetails.customerAccountedNumber,
            salesPersonName: siteDetails.accountManagerFullName,
          }
        : undefined;

      // Connect to socket with site details
      initAIAssistantSocket(stationId, handleSocketMessage, handleConnect, handleDisconnect, socketSiteDetails).catch(
        error => {
          console.error('Failed to initialize AI Assistant socket:', error);
        },
      );
    };

    initializeSocket();

    return () => {
      // Only close if we're switching stations (not on unmount)
      // The cleanup will be handled by the station change detection in initAIAssistantSocket
    };
  }, [chatbotStore, stationStore?.currentStation?.id]);
};
